import { useRef, useState, useEffect } from 'react';
import { Bell, X, CheckCheck, Trash2, ShoppingBag, ChefHat, DollarSign, Smartphone, AlertCircle } from 'lucide-react';
import { useNotifications, type AppNotification } from '../../hooks/useNotifications';

// ── Types ─────────────────────────────────────────────────────────────────────

const TYPE_META: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
  new_order:       { icon: ShoppingBag,  color: 'text-blue-600',    bg: 'bg-blue-100'    },
  self_order:      { icon: Smartphone,   color: 'text-violet-600',  bg: 'bg-violet-100'  },
  order_ready:     { icon: ChefHat,      color: 'text-emerald-600', bg: 'bg-emerald-100' },
  order_paid:      { icon: DollarSign,   color: 'text-green-600',   bg: 'bg-green-100'   },
  order_cancelled: { icon: AlertCircle,  color: 'text-red-600',     bg: 'bg-red-100'     },
};

function fallbackMeta(type: string) {
  return TYPE_META[type] ?? { icon: Bell, color: 'text-gray-500', bg: 'bg-gray-100' };
}

function timeAgo(iso: string) {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)   return 'ahora';
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400)return `${Math.floor(secs / 3600)}h`;
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
}

// ── Item ──────────────────────────────────────────────────────────────────────

function NotificationItem({ n, onRead, onDismiss }: {
  n: AppNotification;
  onRead: (id: number) => void;
  onDismiss: (id: number) => void;
}) {
  const meta = fallbackMeta(n.type);
  const Icon = meta.icon;
  const isUnread = !n.read_at;

  return (
    <div
      className={`group flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer hover:bg-muted/30 ${isUnread ? 'bg-primary/5' : ''}`}
      onClick={() => isUnread && onRead(n.id)}
    >
      {/* Icon */}
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${meta.bg}`}>
        <Icon size={16} className={meta.color} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${isUnread ? 'font-semibold text-text' : 'font-medium text-text-muted'}`}>
            {n.title}
          </p>
          <span className="shrink-0 text-[10px] text-text-muted tabular-nums">{timeAgo(n.created_at)}</span>
        </div>
        {n.body && (
          <p className="mt-0.5 text-xs text-text-muted leading-snug">{n.body}</p>
        )}
      </div>

      {/* Unread dot + dismiss */}
      <div className="flex shrink-0 flex-col items-center gap-1.5">
        {isUnread && <span className="h-2 w-2 rounded-full bg-primary" />}
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onDismiss(n.id); }}
          className="hidden h-5 w-5 items-center justify-center rounded-full text-text-muted hover:bg-muted group-hover:flex"
          aria-label="Descartar"
        >
          <X size={11} />
        </button>
      </div>
    </div>
  );
}

// ── Bell + Dropdown ───────────────────────────────────────────────────────────

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { notifications, unreadCount, isLoading, markRead, markAllRead, clearRead, dismiss } = useNotifications();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-border transition-colors hover:bg-primary/10 hover:text-primary"
        aria-label={`Notificaciones${unreadCount > 0 ? ` (${unreadCount} sin leer)` : ''}`}
        aria-expanded={open}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[0.55rem] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
          role="region"
          aria-label="Panel de notificaciones"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-text">Notificaciones</h3>
              {unreadCount > 0 && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-600">
                  {unreadCount} nuevas
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead()}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10 transition"
                  title="Marcar todas como leídas"
                >
                  <CheckCheck size={13} /> Todas leídas
                </button>
              )}
              {notifications.some(n => n.read_at) && (
                <button
                  type="button"
                  onClick={() => clearRead()}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-text-muted hover:bg-muted/50 transition"
                  title="Limpiar leídas"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto divide-y divide-border/50">
            {isLoading ? (
              <div className="space-y-3 px-4 py-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-40 animate-pulse rounded bg-muted" />
                      <div className="h-3 w-24 animate-pulse rounded bg-muted" />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-text-muted">
                <Bell size={32} strokeWidth={1} />
                <p className="text-sm">Sin notificaciones</p>
              </div>
            ) : (
              notifications.map(n => (
                <NotificationItem
                  key={n.id}
                  n={n}
                  onRead={markRead}
                  onDismiss={dismiss}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
